import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import defaultShield from '@sorare/core/src/assets/club/shield_none.png';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { withFragments } from '@sorare/core/src/lib/gql';

import { getSeasonBonusRule_so5Rules } from './__generated__/getSeasonBonusRule.graphql';

export const getSeasonBonusRule = withFragments(
  (rules: getSeasonBonusRule_so5Rules, error: string) => {
    const rule = rules.seasonBonusForSpecificTeams;
    if (!rule || (!rule.min && !rule.max)) {
      return null;
    }
    const { min, max, teams } = rule;
    const displayMinAndMax = min && max ? 'between' : 'max';

    return {
      id: 'seasonBonus',
      label: (
        <FormattedMessage
          id="Rules.SeasonBonusForSpecificTeamsRule.label"
          defaultMessage="{display, select, between {Between {min} and {max}} min {Min {min}} other {Max {max}}} in-season cards"
          values={{ display: min && !max ? 'min' : displayMinAndMax, min, max }}
        />
      ),
      description: (
        <Vertical as="span" gap={0}>
          {teams.map(team => (
            <Horizontal key={team.slug} as="span">
              <img
                src={team.pictureUrl || defaultShield}
                alt=""
                width="16"
                height="16"
              />
              {team.name}
            </Horizontal>
          ))}
        </Vertical>
      ),
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.0163 11.9714L13.0544 13.0095V14.9619L12.0163 16H9.15919L8.11157 14.9619L9.07348 14L9.82586 14.7619H11.3306L11.6544 14.4381V13.5333L11.2925 13.1714H8.43538V9.5238H12.7973V10.7619H9.73062V11.9714H12.0163Z"
            fill="url(#paint0_linear_371_1115)"
          />
          <path
            d="M3.89668 13.6476L3.5062 14.0381V14.7619H6.83953V16H2.11572V13.5429L3.07763 12.5809L5.05858 12.1333L5.44906 11.7429V11.0762L5.13477 10.7619H3.71572L3.05858 11.419L2.11572 10.4762L3.0681 9.5238H5.79191L6.83953 10.5714V12.2381L5.87763 13.2L3.89668 13.6476Z"
            fill="url(#paint1_linear_371_1115)"
          />
          <path
            d="M13.1686 3.85714V5.09524H12.4829V6.47619H11.0924V5.09524H8.10193V3.59048L10.8162 0L12.4829 0V3.85714H13.1686ZM9.3305 3.85714H11.0924V1.48571L9.3305 3.85714Z"
            fill="url(#paint2_linear_371_1115)"
          />
          <path
            d="M3.89668 4.12381L3.5062 4.51429V5.23809H6.83953V6.47619H2.11572V4.01905L3.07763 3.05714L5.05858 2.60952L5.44906 2.21905V1.55238L5.13477 1.2381L3.71572 1.2381L3.05858 1.89524L2.11572 0.95238L3.0681 0L5.79191 0L6.83953 1.04762V2.71429L5.87763 3.67619L3.89668 4.12381Z"
            fill="url(#paint3_linear_371_1115)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_371_1115"
              x1="8.11157"
              y1="16"
              x2="13.5317"
              y2="15.5723"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#F8D3DA" />
              <stop offset="0.283178" stopColor="#B3A9F4" />
              <stop offset="0.5401" stopColor="#FBE9FB" />
              <stop offset="1" stopColor="#4F94FD" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_371_1115"
              x1="2.11572"
              y1="16"
              x2="7.29845"
              y2="15.6091"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#F8D3DA" />
              <stop offset="0.283178" stopColor="#B3A9F4" />
              <stop offset="0.5401" stopColor="#FBE9FB" />
              <stop offset="1" stopColor="#4F94FD" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_371_1115"
              x1="8.10193"
              y1="6.47619"
              x2="13.6561"
              y2="6.02693"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#F8D3DA" />
              <stop offset="0.283178" stopColor="#B3A9F4" />
              <stop offset="0.5401" stopColor="#FBE9FB" />
              <stop offset="1" stopColor="#4F94FD" />
            </linearGradient>
            <linearGradient
              id="paint3_linear_371_1115"
              x1="2.11572"
              y1="6.47619"
              x2="7.29845"
              y2="6.08534"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#F8D3DA" />
              <stop offset="0.283178" stopColor="#B3A9F4" />
              <stop offset="0.5401" stopColor="#FBE9FB" />
              <stop offset="1" stopColor="#4F94FD" />
            </linearGradient>
          </defs>
        </svg>
      ),
      error,
    };
  },
  {
    so5Rules: gql`
      fragment getSeasonBonusRule_so5Rules on So5Rules {
        id
        seasonBonusForSpecificTeams {
          min
          max
          teams {
            slug
            name
            pictureUrl
          }
        }
      }
    ` as TypedDocumentNode<getSeasonBonusRule_so5Rules>,
  }
);
